export const sampleBookMeta = {
  title: "Lucas & Emma",
  subtitle: "Our Love in Texts",
  year: 2024,
  yearbookType: "couple",
  dedication: "To every 'goodnight' that turned into a 3am conversation — this book is proof that the best love stories are written one text at a time.",
};

export const sampleCharacters = [
  {
    name: "Lucas",
    bio: "The one who sends voice notes instead of texts, plans surprises three weeks in advance, and has never once won an argument about where to eat. His love language is sending memes at 2am and remembering the little things Emma mentions in passing.",
    traits: ["Hopeless Romantic", "Planner", "Night Owl"],
    signature_quote: "I'm not lost, I'm taking the scenic route",
    story_count: 12,
  },
  {
    name: "Emma",
    bio: "The queen of perfectly-timed comebacks, accidental photo masterpieces, and dramatic retelling of mundane events. She pretends to hate surprises but screenshots every sweet message Lucas sends and saves them in a folder called 'evidence.'",
    traits: ["Quick Wit", "Storyteller", "Secret Softie"],
    signature_quote: "I'm not crying, you're crying",
    story_count: 12,
  },
];

export const sampleChapters = [
  { index: 0, title: "The First Text", title_emoji: "\u{1F4F1}", date: "Jan 14, 2024", is_sample: true },
  { index: 1, title: "Coffee Shop Confessions", title_emoji: "\u{2615}", date: "Jan 28, 2024", is_sample: false },
  { index: 2, title: "The Playlist War", title_emoji: "\u{1F3B5}", date: "Feb 10, 2024", is_sample: false },
  { index: 3, title: "Valentine's Day Chaos", title_emoji: "\u{1F338}", date: "Feb 14, 2024", is_sample: true },
  { index: 4, title: "Meeting the Parents", title_emoji: "\u{1F62C}", date: "Mar 2, 2024", is_sample: false },
  { index: 5, title: "The IKEA Survival Guide", title_emoji: "\u{1F6CB}", date: "Mar 18, 2024", is_sample: false },
  { index: 6, title: "Cooking Disaster", title_emoji: "\u{1F525}", date: "Apr 5, 2024", is_sample: false },
  { index: 7, title: "Road Trip Revelations", title_emoji: "\u{1F697}", date: "Apr 20, 2024", is_sample: false },
  { index: 8, title: "The Dog Debate", title_emoji: "\u{1F436}", date: "May 8, 2024", is_sample: false },
  { index: 9, title: "Airport Countdown", title_emoji: "\u{2708}\u{FE0F}", date: "May 22, 2024", is_sample: false },
  { index: 10, title: "Summer Nights", title_emoji: "\u{1F305}", date: "Jun 15, 2024", is_sample: false },
  { index: 11, title: "The Apology Olympics", title_emoji: "\u{1F605}", date: "Jul 3, 2024", is_sample: false },
  { index: 12, title: "Tuesday Night Ramen", title_emoji: "\u{1F35C}", date: "Aug 12, 2024", is_sample: true },
  { index: 13, title: "Birthday Surprise", title_emoji: "\u{1F382}", date: "Sep 1, 2024", is_sample: false },
  { index: 14, title: "The Haunted House", title_emoji: "\u{1F383}", date: "Oct 28, 2024", is_sample: false },
];

export const sampleStories = [
  {
    story_id: "sample-couple-001",
    chapter_index: 0,
    title: "The First Text",
    title_emoji: "\u{1F4F1}",
    date: "January 14, 2024",
    time_range: "11:42 PM - 2:15 AM",
    participants: ["Lucas", "Emma"],
    tags: ["First Conversation", "Late Night", "Butterflies"],
    narrative: `It started, as most modern love stories do, with a notification at 11:42 on a Sunday night. [[Lucas|Lucas]] had spent the better part of an hour composing what he hoped was the perfect opening message — casual enough to seem effortless, interesting enough to warrant a reply. He'd typed and deleted seventeen variations before settling on something that would haunt him for months to come.

"Hey, so I have to ask — was that really your dog in your profile pic or did you borrow someone else's golden retriever for clout?"

[[Emma|Emma]] saw the message immediately. She was already in bed, doom-scrolling through her phone, when the notification popped up. She laughed out loud — actually laughed, not just exhaled slightly through her nose. She screenshot it and sent it to her group chat with the caption "okay this one might be different."

Her reply came four minutes later — long enough to seem unbothered, short enough to keep the conversation alive. "His name is Captain Waffles and he is MINE, thank you very much. I have receipts."

What followed was three hours of increasingly unhinged conversation. They debated whether cereal was soup (Lucas: absolutely. Emma: "I'm going to pretend you didn't say that"). They discovered they'd been at the same concert two years ago, standing in the same section. [[Lucas|Lucas]] confessed he'd watched her Instagram story about homemade pasta four times. [[Emma|Emma]] admitted she'd already looked up his Spotify and judged his taste in music.

"Your top artist is Coldplay," she wrote at 1:47 AM. "I need you to explain yourself."

"Coldplay slander at this hour? On a school night?" he fired back. "Some of us have layers, Emma."

By 2 AM, they'd exchanged phone numbers, planned a hypothetical trip to Japan, and established that pineapple on pizza was, in fact, acceptable (a hill Lucas was willing to die on). When [[Emma|Emma]] finally said goodnight — "I have to be a functioning human in 6 hours" — [[Lucas|Lucas]] stared at his ceiling for another twenty minutes, grinning like an idiot.

He saved her contact as "Emma \u{2615}" — because she'd mentioned she couldn't function without coffee. It was a small detail. He'd remember all of them.`,
  },
  {
    story_id: "sample-couple-003",
    chapter_index: 3,
    title: "Valentine's Day Chaos",
    title_emoji: "\u{1F338}",
    date: "February 14, 2024",
    time_range: "6:30 AM - 11:58 PM",
    participants: ["Lucas", "Emma"],
    tags: ["Valentine's Day", "Surprises", "Plot Twist"],
    narrative: `[[Lucas|Lucas]] had been planning this for exactly nineteen days. He had a spreadsheet. He had a backup plan, and a backup to the backup plan. What he did not have, as of 6:30 AM on February 14th, was a reservation — because the restaurant had just called to tell him about a "plumbing emergency" that would keep them closed for the evening.

"No no no no no," he typed to his best friend at 6:32 AM. "THE PLAN IS RUINED."

What [[Lucas|Lucas]] didn't know was that [[Emma|Emma]] had also been planning something. She'd spent the past two weeks secretly learning to make his favorite dish — her grandmother's lasagna recipe, the one she'd always claimed was "too complicated" to attempt. Her kitchen looked like a crime scene by noon.

"How hard can lasagna be?" she had texted her sister at 8 AM. By 11 AM, the update was simply: "Send help."

The comedy of errors continued throughout the day. Lucas scrambled to book a new restaurant, finding only a 5:00 PM slot at a place forty minutes away. Emma burned her first attempt at lasagna, set off the smoke alarm, and had to open every window in her apartment in February. Their text exchange throughout was a masterclass in misdirection.

"Can't wait for tonight \u{2764}\u{FE0F}" [[Emma|Emma]] wrote at 2 PM, hiding the flour in her hair during a video call that she quickly declined.

"Same! Just, uh, wear something warm," [[Lucas|Lucas]] replied cryptically, not mentioning he was currently on hold with restaurant number seven.

When he showed up at her door at 6 PM with flowers and a slightly panicked expression, he found her in an apron, the apartment smelling incredible, candles everywhere, and a homemade lasagna that looked — against all odds — perfect.

"I made dinner," she said, trying to look casual, as if she hadn't been crying over onions and dough for six hours. "Surprise?"

[[Lucas|Lucas]] stood in the doorway for a full five seconds, flowers in hand, restaurant confirmation still open on his phone. Then he started laughing. Then she started laughing. Then he showed her the spreadsheet.

"You made a SPREADSHEET?" she wheezed, scrolling through tabs labeled "Outfit Options," "Conversation Topics (Backup)," and "Emergency Florist Numbers."

"You learned to make LASAGNA?" he countered, pulling up her text to her sister that she'd accidentally sent to him too — "If this lasagna kills us both it was worth it for the aesthetic."

They ate the lasagna. It was slightly overcooked on the edges and absolutely perfect in the middle, which [[Emma|Emma]] later said was a metaphor for their relationship. [[Lucas|Lucas]] said that was the most Emma thing she'd ever said. She threw a breadstick at him.

At 11:58 PM, Lucas sent one final text: "Best Valentine's Day I've ever had. Even without the spreadsheet working out."

Emma replied: "Best Valentine's Day I've ever had. Even with the smoke alarm."`,
  },
  {
    story_id: "sample-couple-012",
    chapter_index: 12,
    title: "Tuesday Night Ramen",
    title_emoji: "\u{1F35C}",
    date: "August 12, 2024",
    time_range: "7:22 PM - 10:45 PM",
    participants: ["Lucas", "Emma"],
    tags: ["Ordinary Magic", "Comfort", "Home"],
    narrative: `There was nothing special about a Tuesday in August. No anniversary, no milestone, no carefully planned surprise. Just [[Emma|Emma]] getting home from work twenty minutes late, kicking off her shoes at the door, and finding [[Lucas|Lucas]] already in the kitchen with two packs of instant ramen and a look of deep concentration.

"I'm adding an egg," he announced, as if this was a culinary breakthrough. "And sesame oil. We're being fancy tonight."

"Fancy ramen on a Tuesday," [[Emma|Emma]] said, dropping her bag on the counter. "We've really made it."

They'd been together seven months by then. Long enough for the butterflies to evolve into something quieter — that warm, settled feeling of knowing exactly how someone takes their coffee (Lucas: oat milk, two sugars, in the blue mug) and which side of the bed they'd gravitate toward even in a hotel room.

[[Emma|Emma]] changed into his old college hoodie — the gray one she'd stolen so long ago he'd stopped pretending he wanted it back. She sat cross-legged on the kitchen counter, stealing toppings while he cooked, and told him about a meeting that had gone sideways.

"And then she said — I'm not making this up — she said 'let's circle back to synergize our core deliverables,'" Emma recounted, using air quotes. "I almost quit on the spot."

"You should have stood up and said 'I'm circling forward,'" [[Lucas|Lucas]] suggested, adding the eggs to the boiling water with the focus of a surgeon. "Just walked right out the door."

"'I'm circling forward,'" she repeated, laughing. "That's going in my resignation letter when the time comes."

They ate on the couch with the TV on but the volume low — some cooking competition neither of them was really watching. [[Lucas|Lucas]] had his feet on the coffee table, which Emma had given up fighting about in March. Emma had her legs across his lap, scrolling through her phone and occasionally showing him posts.

"Look at this cat," she'd say every few minutes, turning her screen toward him.

"That IS a good cat," he'd confirm each time, with complete sincerity.

At some point, [[Emma|Emma]] put her phone down, rested her head on his shoulder, and said — so quietly he almost missed it — "This is my favorite thing."

"Ramen?" he asked.

"No." She paused. "Well, yes. But also just... this. Tuesday. You. Regular boring Tuesday with you."

[[Lucas|Lucas]] didn't say anything for a moment. He just pulled her a little closer, pressed a kiss to the top of her head, and thought about how the best parts of loving someone aren't the grand gestures or the spreadsheet-planned Valentine's Days. They're the Tuesdays. The instant ramen with a fancy egg. The stolen hoodie. The showing-each-other-cats-on-the-couch.

He picked up his phone later that night, after Emma had fallen asleep mid-episode, and typed a note to himself: "August 12. Tuesday. Ramen. She said I'm her favorite thing. Remember this one."

He would.`,
  },
];
